import is from '@sindresorhus/is';
import clsx from 'clsx';
import type { FunctionComponent, HTMLAttributes, ReactNode } from 'react';

import { IconChevronLargeRight } from '../icons';

export type BreadcrumbItem = {
    readonly label: ReactNode | string;
    readonly onClick?: () => void;
};

export type BreadcrumbsProps = HTMLAttributes<HTMLDivElement> & {
    readonly className?: string;
    readonly items: readonly BreadcrumbItem[];
    /** Extra classes on every crumb, e.g. to override the default `text-xxs`. */
    readonly labelClassName?: string;
    readonly lastItemIsCurrent?: boolean;
};

export const Breadcrumbs: FunctionComponent<BreadcrumbsProps> = ({
    className,
    items,
    labelClassName,
    lastItemIsCurrent = true,
    ...props
}) => {
    return (
        <div className={clsx('flex items-center gap-m font-sans', className)} {...props}>
            {items.map((item, index) => {
                const isLast = index === items.length - 1;
                const isCurrent = isLast && lastItemIsCurrent;

                return (
                    // biome-ignore lint/suspicious/noArrayIndexKey: suppressed due to migration
                    <div key={index} className="flex items-center gap-m">
                        {is.function(item.onClick) && !isCurrent ? (
                            // biome-ignore lint/a11y/useButtonType: suppressed due to migration
                            <button
                                className={clsx(
                                    'bg-transparent border-none p-0 text-body-xxs font-medium text-text-content-subtle cursor-pointer hover:text-text-muted',
                                    labelClassName,
                                )}
                                onClick={item.onClick}
                            >
                                {item.label}
                            </button>
                        ) : (
                            <span
                                aria-current={isCurrent ? 'page' : undefined}
                                className={clsx('text-body-xxs font-medium text-text-content-subtle', labelClassName)}
                            >
                                {item.label}
                            </span>
                        )}
                        {!isLast && (
                            <IconChevronLargeRight className="text-text-disabled text-sm h-[0.875rem] w-[0.875rem]" />
                        )}
                    </div>
                );
            })}
        </div>
    );
};
